import { Validation, ValidationFunction, ValidationResult } from './validation-types'
import React from 'react'

const VALID_VALIDITY_STATE: ValidityState = {
  badInput: false,
  customError: false,
  patternMismatch: false,
  rangeOverflow: false,
  rangeUnderflow: false,
  stepMismatch: false,
  tooLong: false,
  tooShort: false,
  typeMismatch: false,
  valueMissing: false,
  valid: true
}

const CUSTOM_VALIDITY_STATE: ValidityState = {
  ...VALID_VALIDITY_STATE,
  customError: true,
  valid: false
}

const DEFAULT_VALIDATION_RESULT: ValidationResult = {
  isInvalid: false,
  validationDetails: VALID_VALIDITY_STATE,
  validationErrors: []
}

interface FormValidationProps<T> extends Validation<T> {
  value: T
}

export interface FormWarningState {
  /** Realtime validation results, updated as the user edits the value. */
  realtimeValidation: ValidationResult
  /** Currently displayed validation results, updated when the user commits their changes. */
  displayValidation: ValidationResult
  /** Updates the current validation result. Not displayed to the user until `commitValidation` is called. */
  updateValidation(result: ValidationResult): void
  /** Resets the displayed validation state to valid when the user resets the form. */
  resetValidation(): void
  /** Commits the realtime validation so it is displayed to the user. */
  commitValidation(): void
  warningMessage: string
}

export function useFormWarningState<T>(props: FormValidationProps<T>): FormWarningState {
  let { isInvalid, value, validate } = props

  // If the isInvalid prop is true, update validation result in realtime (controlled).
  const controlledError: ValidationResult | null = isInvalid
    ? {
        isInvalid: true,
        validationErrors: [],
        validationDetails: CUSTOM_VALIDITY_STATE
      }
    : null

  // Perform custom client side validation.
  let clientError: ValidationResult | null = React.useMemo(() => {
    if (validate) {
      return getValidationResult(runValidate(validate, value))
    } else {
      return null
    }
  }, [validate, value])

  // Track the next validation state in a ref until commitValidation is called.
  let nextValidation = React.useRef(DEFAULT_VALIDATION_RESULT)
  let [currentValidity, setCurrentValidity] = React.useState(DEFAULT_VALIDATION_RESULT)

  let lastError = React.useRef(DEFAULT_VALIDATION_RESULT)
  let commitValidation = () => {
    if (!commitQueued) {
      return
    }

    setCommitQueued(false)
    let error = clientError || nextValidation.current
    if (!isEqualValidation(error, lastError.current)) {
      lastError.current = error
      setCurrentValidity(error)
    }
  }

  let [commitQueued, setCommitQueued] = React.useState(false)
  React.useEffect(commitValidation)

  // realtimeValidation is used to update the native input element's state based on custom validation logic.
  // displayValidation is the currently displayed validation state that the user sees (e.g. on input change/form submit).
  // With validationBehavior="aria", all errors are displayed in realtime rather than on submit.
  let realtimeValidation = controlledError || clientError || DEFAULT_VALIDATION_RESULT
  let displayValidation = controlledError || currentValidity

  return {
    realtimeValidation,
    displayValidation,
    updateValidation(value) {
      // If validationBehavior is 'aria', update in realtime. Otherwise, store in a ref until commit.
      nextValidation.current = value
    },
    resetValidation() {
      // Update the currently displayed validation state to valid on form reset,
      // even if the native validity says it isn't. It'll show again on the next form submit.
      let error = DEFAULT_VALIDATION_RESULT
      if (!isEqualValidation(error, lastError.current)) {
        lastError.current = error
        setCurrentValidity(error)
      }

      // Do not commit validation after the next render. This avoids a condition where
      // useSelect calls commitValidation inside an onReset handler.
      setCommitQueued(false)
    },
    commitValidation() {
      // Commit validation state so the user sees it on blur/change/submit. Also clear any server errors.
      // Wait until after the next render to commit so that the latest value has been validated.
      setCommitQueued(true)
    },
    warningMessage: displayValidation.validationErrors[0] ?? (displayValidation.isInvalid ? 'invalid' : '')
  }
}

function asArray<T>(v: T | T[]): T[] {
  if (!v) {
    return []
  }

  return Array.isArray(v) ? v : [v]
}

function runValidate<T>(validate: ValidationFunction<T>, value: T): string[] {
  if (typeof validate === 'function') {
    let e = validate(value)
    if (e && typeof e !== 'boolean') {
      return asArray(e)
    }
  }

  return []
}

function getValidationResult(errors: string[]): ValidationResult | null {
  return errors.length
    ? {
        isInvalid: true,
        validationErrors: errors,
        validationDetails: CUSTOM_VALIDITY_STATE
      }
    : null
}

function isEqualValidation(a: ValidationResult | null, b: ValidationResult | null): boolean {
  if (a === b) {
    return true
  }

  return (
    !!a &&
    !!b &&
    a.isInvalid === b.isInvalid &&
    a.validationErrors.length === b.validationErrors.length &&
    a.validationErrors.every((a, i) => a === b.validationErrors[i]) &&
    Object.entries(a.validationDetails).every(([k, v]) => b.validationDetails[k] === v)
  )
}
